
import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Wrench, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Memoize the scroll handler to improve performance
  const handleScroll = useCallback(() => {
    if (window.scrollY > 10) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      // If on another page, navigate to home first
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled ? "bg-white shadow-md py-2" : "bg-transparent py-4"
      )}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/" className="hidden md:block">
            <img 
              src="/lovable-uploads/9660a1c7-e321-4122-b380-58c44ed97180.png" 
              alt="AD Email Lab Logo" 
              className="h-12"
              width="48"
              height="48"
              loading="eager"
            />
          </Link>
          <div className="flex gap-2">
            <Button 
              onClick={() => window.open('https://ad-email-lab-builder.lovable.app/', '_blank')} 
              variant="outline" 
              className="group relative inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold border-2 border-primary/30 bg-gradient-to-r from-white to-blue-50 hover:from-primary/5 hover:to-primary/10 hover:border-primary/50 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary/20 rounded-xl overflow-hidden"
            >
              {/* Background animation effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
              
              {/* Icon with enhanced styling */}
              <div className="relative z-10 p-1 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors duration-300">
                <Wrench className="h-3 w-3 text-primary" />
              </div>
              
              {/* Text content */}
              <span className="relative z-10 text-secondary group-hover:text-primary transition-colors duration-300 text-center leading-tight">
                Email Builder
              </span>
              
              {/* Subtle shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out"></div>
            </Button>
            
            <Button 
              onClick={() => window.open('https://ad-email-image-editor.lovable.app/', '_blank')} 
              variant="outline" 
              className="group relative inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold border-2 border-primary/30 bg-gradient-to-r from-white to-green-50 hover:from-primary/5 hover:to-primary/10 hover:border-primary/50 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary/20 rounded-xl overflow-hidden"
            >
              {/* Background animation effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
              
              {/* Icon with enhanced styling */}
              <div className="relative z-10 p-1 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors duration-300">
                <ImageIcon className="h-3 w-3 text-primary" />
              </div>
              
              {/* Text content */}
              <span className="relative z-10 text-secondary group-hover:text-primary transition-colors duration-300 text-center leading-tight">
                Image Editor
              </span>
              
              {/* Subtle shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out"></div>
            </Button>
          </div>
        </div>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8">
          <Link
            to="/"
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            Home
          </Link>
          <Link
            to="/about"
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            About Me
          </Link>
          <Link
            to="/blog"
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            Blog
          </Link>
          <button 
            onClick={() => scrollToSection('benefits')}
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            Benefits
          </button>
          <button 
            onClick={() => scrollToSection('templates')}
            className="text-secondary hover:text-primary font-medium transition-colors"
          >
            Portfolio
          </button>
          <Button
            onClick={() => scrollToSection('contact')}
            variant="default"
            className="bg-primary hover:bg-primary/90 text-white"
          >
            Contact Me
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <button
            onClick={toggleMobileMenu}
            className="text-secondary hover:text-primary p-2"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-md animate-fade-in">
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <Link
              to="/"
              className="text-secondary hover:text-primary py-2 font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-secondary hover:text-primary py-2 font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Me
            </Link>
            <Link
              to="/blog"
              className="text-secondary hover:text-primary py-2 font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('benefits')}
              className="text-secondary hover:text-primary py-2 font-medium transition-colors text-left"
            >
              Benefits
            </button>
            <button
              onClick={() => scrollToSection('templates')}
              className="text-secondary hover:text-primary py-2 font-medium transition-colors text-left"
            >
              Portfolio
            </button>
            <Button
              onClick={() => scrollToSection('contact')}
              variant="default"
              className="bg-primary hover:bg-primary/90 text-white w-full"
            >
              Contact Me
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
